import {
  ErrorResponse,
  InMemoryWebStorage,
  type User,
  UserManager,
  WebStorageStateStore,
} from 'oidc-client-ts';

import { AuthStore, principalFromAccessToken, safeReturnTo } from './store.js';
import type { AuthController, AuthState, CallbackResult, LoginOptions } from './types.js';

/**
 * Keycloak public client with Authorization Code + PKCE (T-01, FTD §1.4, §15), on `oidc-client-ts`.
 *
 * - Tokens live in memory only: the user store is `InMemoryWebStorage`; nothing is written to
 *   `localStorage`, `sessionStorage`, IndexedDB, or cookies.
 * - The redirect round trip needs its PKCE `code_verifier` and `state` to survive one navigation, so
 *   only that transient request state uses `sessionStorage` (tab-scoped, removed by the callback and
 *   by `clearStaleState`). It never holds a token.
 * - A reload has no tokens, so `restoreSession` does a top-level `prompt=none` redirect against the
 *   Keycloak SSO session. No hidden iframe is used: the apps send `frame-ancestors 'none'`, and
 *   third-party cookie blocking would break iframe renewal anyway.
 * - Renewal uses the refresh-token grant; Keycloak rotates the refresh token on every use
 *   (`revokeRefreshToken`, `refreshTokenMaxReuse: 0`). There is no iframe fallback.
 * - Logout removes the user, runs the sign-out listeners (cache clears), and calls end-session.
 */

export interface OidcAuthConfig {
  /** Realm issuer, for example `http://127.0.0.1:58080/realms/saay`. */
  readonly authority: string;
  /** `saay-admin-web`, `saay-merchant-web`, or `saay-buyer-web`. */
  readonly clientId: string;
  /** The app origin, for example `http://127.0.0.1:3001`; redirect URIs are derived from it. */
  readonly origin: string;
  /** Callback path registered in the realm (default `/auth/callback`). */
  readonly callbackPath?: string;
  /**
   * OIDC `max_age` on login and on the reload restore: Keycloak asks for credentials again when the
   * SSO session's last login is older (W4 review decision: admin-web 1800, merchant-web 50400).
   * It also stops a long buyer-web remember-me SSO session in the same browser from silently
   * signing in a staff app. Unset for buyer-web.
   */
  readonly maxAgeSeconds?: number;
  /** Renew this many seconds before the access token expires (default 60). */
  readonly renewBeforeExpirySeconds?: number;
  /** Test seam; defaults to `new UserManager(settings)`. */
  readonly createUserManager?: (
    settings: ConstructorParameters<typeof UserManager>[0],
  ) => UserManager;
  /** Where the transient PKCE request state lives (default `sessionStorage`); a test seam. */
  readonly stateStorage?: Storage;
}

interface RedirectState {
  readonly returnTo: string;
  readonly silent: boolean;
}

function isRedirectState(value: unknown): value is RedirectState {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as RedirectState).returnTo === 'string'
  );
}

export function createOidcAuth(config: OidcAuthConfig): AuthController {
  const store = new AuthStore();
  const callbackPath = config.callbackPath ?? '/auth/callback';
  const origin = config.origin.replace(/\/+$/, '');
  const settings = {
    authority: config.authority,
    automaticSilentRenew: false,
    client_id: config.clientId,
    filterProtocolClaims: true,
    loadUserInfo: false,
    monitorSession: false,
    post_logout_redirect_uri: `${origin}/`,
    redirect_uri: `${origin}${callbackPath}`,
    response_type: 'code',
    scope: 'openid',
    stateStore: new WebStorageStateStore({
      prefix: `saay.oidc.${config.clientId}.`,
      store: config.stateStorage ?? globalThis.sessionStorage,
    }),
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  } satisfies ConstructorParameters<typeof UserManager>[0];
  const manager = config.createUserManager?.(settings) ?? new UserManager(settings);

  let user: User | null = null;
  let renewTimer: ReturnType<typeof setTimeout> | undefined;
  let refreshing: Promise<string | null> | undefined;

  function stateFor(next: User | null): AuthState {
    return next === null
      ? { principal: null, status: 'unauthenticated' }
      : {
          principal: principalFromAccessToken(
            next.access_token,
            next.profile as Record<string, unknown>,
          ),
          status: 'authenticated',
        };
  }

  function scheduleRenewal(): void {
    clearTimeout(renewTimer);
    const expiresIn = user?.expires_in;
    if (user === null || expiresIn === undefined) {
      return;
    }
    const lead = config.renewBeforeExpirySeconds ?? 60;
    const delay = Math.max(0, (expiresIn - lead) * 1000);
    renewTimer = setTimeout(() => {
      void refreshAccessToken();
    }, delay);
  }

  function accept(next: User): void {
    user = next;
    store.set(stateFor(next));
    scheduleRenewal();
  }

  async function end(status: 'unauthenticated' | 'session-expired'): Promise<void> {
    clearTimeout(renewTimer);
    user = null;
    await manager.removeUser().catch(() => undefined);
    store.signedOut(status);
  }

  async function refreshAccessToken(): Promise<string | null> {
    if (user?.refresh_token === undefined) {
      return null;
    }
    refreshing ??= (async () => {
      try {
        // With a refresh token, signinSilent uses the refresh-token grant (no iframe).
        const renewed = await manager.signinSilent();
        if (renewed === null) {
          await end('session-expired');
          return null;
        }
        accept(renewed);
        return renewed.access_token;
      } catch {
        await end('session-expired');
        return null;
      } finally {
        refreshing = undefined;
      }
    })();
    return refreshing;
  }

  async function redirect(
    state: RedirectState,
    extra: { readonly extraQueryParams?: Record<string, string>; readonly prompt?: string },
  ): Promise<void> {
    await manager.signinRedirect({
      state,
      ...extra,
      ...(config.maxAgeSeconds === undefined ? {} : { max_age: config.maxAgeSeconds }),
    });
  }

  function currentPage(): string {
    const { hash, pathname, search } = globalThis.location;
    return safeReturnTo(`${pathname}${search}${hash}`);
  }

  return {
    getAccessToken: () => user?.access_token ?? null,
    getState: () => store.getState(),
    handleCallback: async (url?: string): Promise<CallbackResult> => {
      try {
        const signedIn = await manager.signinCallback(url);
        if (signedIn === undefined) {
          await end('unauthenticated');
          return { returnTo: '/' };
        }
        accept(signedIn);
        return {
          returnTo: safeReturnTo(isRedirectState(signedIn.state) ? signedIn.state.returnTo : '/'),
        };
      } catch (error) {
        const returnTo = safeReturnTo(
          error instanceof ErrorResponse && isRedirectState(error.state)
            ? error.state.returnTo
            : '/',
        );
        // `login_required` after `prompt=none` just means there is no SSO session.
        await end('unauthenticated');
        if (error instanceof ErrorResponse && error.error === 'login_required') {
          return { returnTo };
        }
        throw error;
      } finally {
        await manager.clearStaleState().catch(() => undefined);
      }
    },
    login: async (options: LoginOptions = {}) => {
      const returnTo = safeReturnTo(options.returnTo ?? '/');
      await redirect({ returnTo, silent: false }, {});
    },
    startPasswordChange: async (returnTo?: string) => {
      await redirect(
        {
          returnTo: returnTo === undefined ? currentPage() : safeReturnTo(returnTo),
          silent: false,
        },
        { extraQueryParams: { kc_action: 'UPDATE_PASSWORD' } },
      );
    },
    logout: async () => {
      const idToken = user?.id_token;
      await end('unauthenticated');
      await manager.signoutRedirect({
        ...(idToken === undefined ? {} : { id_token_hint: idToken }),
        post_logout_redirect_uri: `${origin}/`,
      });
    },
    markSessionExpired: () => {
      if (store.getState().status === 'authenticated') {
        void end('session-expired');
      }
    },
    onSignOut: (listener) => store.onSignOut(listener),
    refreshAccessToken,
    restoreSession: async (returnTo: string) => {
      if (user !== null) {
        store.set(stateFor(user));
        return;
      }
      await manager.clearStaleState().catch(() => undefined);
      await redirect({ returnTo: safeReturnTo(returnTo), silent: true }, { prompt: 'none' });
    },
    subscribe: (listener) => store.subscribe(listener),
  };
}
